// src/components/AddStudentForm.jsx

import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db, secondaryAuth } from "../firebase-config";

const SUBSCRIPTION_PLANS = {
  "299-1-month": {
    name: "₱299 — 1 Month Promo",
    amount: 299,
    months: 1,
  },
  "599-6-months": {
    name: "₱599 — 6 Months Promo",
    amount: 599,
    months: 6,
  },
  "999-1-year": {
    name: "₱999 — 1 Year Promo",
    amount: 999,
    months: 12,
  },
};

export default function AddStudentForm({ onStudentAdded }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedPlan = subscriptionPlan
    ? SUBSCRIPTION_PLANS[subscriptionPlan]
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password || !subscriptionPlan) {
      alert("Please complete all required fields.");
      return;
    }

    setLoading(true);

    try {
      const plan = SUBSCRIPTION_PLANS[subscriptionPlan];

      if (!plan) {
        throw new Error("Invalid subscription plan selected.");
      }

      const subscriptionStartDate = new Date();
      const subscriptionExpirationDate = new Date(
        subscriptionStartDate
      );

      subscriptionExpirationDate.setMonth(
        subscriptionExpirationDate.getMonth() + plan.months
      );

      // Create student without logging out the admin.
      const cred = await createUserWithEmailAndPassword(
        secondaryAuth,
        email.trim(),
        password
      );

      // Create the student profile.
      await setDoc(
        doc(db, "users", cred.user.uid),
        {
          email: email.trim(),
          role: "student",
          enabled: true,
          authorizedCourses: [],
          hasAiAccess: false,

          createdAt: serverTimestamp(),

          subscriptionPlan,
          subscriptionPlanName: plan.name,
          subscriptionAmount: plan.amount,
          subscriptionMonths: plan.months,
          subscriptionStatus: "active",
          subscriptionStartedAt: serverTimestamp(),
          subscriptionStartedAtMs:
            subscriptionStartDate.getTime(),
          subscriptionExpiresAt: Timestamp.fromDate(
            subscriptionExpirationDate
          ),
          subscriptionExpiresAtMs:
            subscriptionExpirationDate.getTime(),
        },
        { merge: true }
      );

      setEmail("");
      setPassword("");
      setSubscriptionPlan("");

      alert(
        `Student created successfully with the ${plan.name} subscription.`
      );

      onStudentAdded?.();
    } catch (err) {
      console.error("Student creation failed:", err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 bg-gray-700 p-4 rounded-lg"
    >
      <div>
        <label className="block text-sm font-medium mb-1">
          Student Email
        </label>

        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-2 rounded bg-gray-600 text-white"
          placeholder="student@email.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Temporary Password
        </label>

        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-2 rounded bg-gray-600 text-white"
          placeholder="Temporary password"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Subscription Plan
        </label>

        <select
          required
          value={subscriptionPlan}
          onChange={(e) =>
            setSubscriptionPlan(e.target.value)
          }
          className="w-full p-2 rounded bg-gray-600 text-white"
        >
          <option value="">
            Select a subscription plan
          </option>

          <option value="299-1-month">
            ₱299 — 1 Month Promo
          </option>

          <option value="599-6-months">
            ₱599 — 6 Months Promo
          </option>

          <option value="999-1-year">
            ₱999 — 1 Year Promo
          </option>
        </select>
      </div>

      {selectedPlan && (
        <div className="rounded-lg border border-green-500/40 bg-green-950/30 p-3">
          <p className="text-xs text-gray-300">
            Selected subscription
          </p>

          <p className="font-bold text-green-400 mt-1">
            {selectedPlan.name}
          </p>

          <p className="text-xs text-gray-400 mt-1">
            Duration: {selectedPlan.months}{" "}
            {selectedPlan.months === 1 ? "month" : "months"}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-2 rounded font-semibold"
      >
        {loading ? "Creating Student..." : "Create Student"}
      </button>
    </form>
  );
}